/**
 * HelpWidget — Floating Help & Entitlements Guide
 * 
 * A single bottom-right floating button that opens a tabbed panel containing:
 *   Tab 1: "How To" — Getting started guides for VenueFlow
 *   Tab 2: "Entitlements" — Full 2026 Commercial Program reference
 * 
 * Replaces the separate HelpWidget + EntitlementsPanel side-tab.
 * Source: 2026_Club_Commercial_Program_Key_Changes_FINAL.pdf
 */

import React, { useState } from "react";
import { 
  ENTITLEMENTS_2026, 
  KEY_CHANGES_2026, 
  KEY_MOMENTS,
  BRANDING_TYPES,
} from "../../constants/entitlements";

// ============================================================
// THEME
// ============================================================
const T = {
  bg: "#0a0a0f",
  bgElevated: "#12121a",
  bgCard: "#1a1a24",
  bgCardHover: "#1e1e2a",
  accent: "#2fd6b5",
  accentHover: "#26b89a",
  lime: "#cdde25",
  green: "#22c55e",
  greenDim: "rgba(34, 197, 94, 0.12)",
  amber: "#fbbf24",
  amberDim: "rgba(251, 191, 36, 0.12)",
  red: "#ef4444",
  redDim: "rgba(239, 68, 68, 0.12)",
  purple: "#a78bfa",
  tealDim: "rgba(47, 214, 181, 0.12)",
  text: "#ffffff",
  textSecondary: "#a1a1aa",
  textMuted: "#71717a",
  textDim: "#6b7280",
  border: "rgba(255, 255, 255, 0.1)",
  borderLight: "rgba(255, 255, 255, 0.06)",
};

// ============================================================
// SVG ICONS
// ============================================================
const Icon = ({ d, size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {typeof d === "string" ? <path d={d} /> : d}
  </svg>
);

const Icons = {
  chevronDown: <Icon d={<polyline points="6 9 12 15 18 9" />} />,
  chevronRight: <Icon d={<polyline points="9 18 15 12 9 6" />} />,
  clock: <Icon d={<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>} />,
  tv: <Icon d={<><rect x="2" y="7" width="20" height="15" rx="2" ry="2" /><polyline points="17 2 12 7 7 2" /></>} />,
  target: <Icon d={<><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>} />,
  flag: <Icon d={<><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></>} />,
  monitor: <Icon d={<><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></>} />,
  layout: <Icon d={<><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></>} />,
  users: <Icon d={<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></>} />,
  star: <Icon d={<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />} />,
};

// ============================================================
// HOW-TO GUIDES DATA
// ============================================================
const GUIDES = [
  {
    id: "upload-content",
    icon: "\u{1F4E4}",
    title: "How do I upload content?",
    steps: [
      "Click 'Upload Content' in the sidebar",
      "Select your venue (Marvel Stadium or MCG)",
      "Choose the content type (LED, IPTV, Screens, etc.)",
      "Drag and drop your files or click to browse",
      "Add any notes for the operations team",
      "Click 'Submit' to complete your upload",
    ],
    tip: "Make sure your files match the required dimensions, naming convention and format shown in each uploader.",
  },
  {
    id: "find-content-id",
    icon: "\u{1F3F7}\uFE0F",
    title: "How do I find my Content ID?",
    steps: [
      "Go to 'MatchFlow Library' in the sidebar",
      "Browse or search for your uploaded content",
      "The Content ID appears on each asset card (e.g. STK_348JJR) or search by the file name",
      "Click on an asset to see full details including the ID",
    ],
    tip: "Use your Content ID when enquiring with your Lumen8 Coordinator about specific assets.",
  },
  {
    id: "schedule-content",
    icon: "\u{1F4C5}",
    title: "How do I schedule content for a match?",
    steps: [
      "Click 'MatchFlow Builder' in the sidebar",
      "Select your fixture from the dropdown",
      "Choose the match phase (Pre-Game, In-Game, Post-Game)",
      "Create a new row. Select content from the dropdown asset picker",
      "You don't need to populate every asset picker. Only allocate content to the row that is intended to play",
      "Arrange the order by dragging items up or down",
      "Your changes save automatically. You can edit, move or delete rows",
      "Once content is Approved by Lumen8, it is locked and ready for production playout",
    ],
    tip: "Content must be uploaded and approved before it can be scheduled in MatchFlow.",
  },
  {
    id: "check-specs",
    icon: "\u{1F4D0}",
    title: "What are the file specifications?",
    steps: [
      "Click 'Lumen8 Specifications' at the bottom of the sidebar",
      "Or note the specs shown in each uploader",
      "Common formats: MP4 (H.264), PNG, JPG",
      "Frame rates are typically 25fps for video",
      "Dimensions vary by screen type - always check before uploading",
    ],
    tip: "Files that don't meet specs will be rejected during upload with a clear error message.",
  },
  {
    id: "view-uploads",
    icon: "\u{1F5BC}\uFE0F",
    title: "How do I view my uploaded content?",
    steps: [
      "Click 'MatchFlow Library' in the sidebar",
      "All your club's uploaded content appears here",
      "Use filters to narrow by content type or date",
      "Click any asset to preview and see details",
      "Use the search bar to find specific assets",
    ],
    tip: "Assets show their approval status - look for the green checkmark for approved content.",
  },
];

// ============================================================
// SHARED UI COMPONENTS
// ============================================================

function RestrictionBadge({ type, children }) {
  const colors = {
    danger: { bg: T.redDim, color: T.red, border: `1px solid ${T.red}33` },
    warning: { bg: T.amberDim, color: T.amber, border: `1px solid ${T.amber}33` },
    info: { bg: T.tealDim, color: T.accent, border: `1px solid ${T.accent}33` },
    success: { bg: T.greenDim, color: T.green, border: `1px solid ${T.green}33` },
  };
  const c = colors[type] || colors.info;
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, padding: "6px 10px", borderRadius: 6,
      background: c.bg, color: c.color, border: c.border, marginBottom: 6, lineHeight: 1.4,
    }}>
      {type === "danger" && "\u{1F6AB} "}{type === "warning" && "\u26A0\uFE0F "}{children}
    </div>
  );
}

function InfoRow({ label, value, highlight, mono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${T.borderLight}` }}>
      <span style={{ color: T.textDim, flex: "0 0 auto", maxWidth: "45%" }}>{label}</span>
      <span style={{ color: highlight ? T.accent : T.text, fontWeight: highlight ? 600 : 500, textAlign: "right", fontFamily: mono ? "monospace" : "inherit" }}>{value}</span>
    </div>
  );
}

function Note({ children, type = "info" }) {
  const colors = { info: T.accent, warning: T.amber, danger: T.red };
  return (
    <div style={{
      fontSize: 11, color: T.textSecondary, marginTop: 8, padding: "8px 10px",
      background: T.bgCard, borderRadius: 6, borderLeft: `3px solid ${colors[type] || T.accent}`,
      lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

function Tag({ children, variant = "default" }) {
  const variants = {
    default: { background: T.bgCard, color: T.textMuted },
    success: { background: T.greenDim, color: T.green },
    warning: { background: T.amberDim, color: T.amber },
    danger: { background: T.redDim, color: T.red },
    teal: { background: T.tealDim, color: T.accent },
  };
  const s = variants[variant] || variants.default;
  return <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, whiteSpace: "nowrap", fontWeight: 600, ...s }}>{children}</span>;
}

function TagList({ children }) {
  return <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>{children}</div>;
}

function BrandingOptions({ options }) {
  if (!options || options.length === 0) return null;
  const variantMap = {
    [BRANDING_TYPES.CLUB_IP]: "warning",
    [BRANDING_TYPES.CO_BRANDED]: "teal",
    [BRANDING_TYPES.COMMERCIAL]: "success",
    [BRANDING_TYPES.CO_BRANDED_OVERLAY]: "teal",
  };
  return (
    <div style={{ marginTop: 6 }}>
      <span style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.5px" }}>Branding Options</span>
      <TagList>{options.map((opt, i) => <Tag key={i} variant={variantMap[opt] || "default"}>{opt}</Tag>)}</TagList>
    </div>
  );
}

function SubHeader({ children }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: T.accent, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8, marginTop: 16 }}>{children}</div>;
}

function AccordionSection({ icon, title, badge, expanded, onToggle, children }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <div
        style={{
          padding: "12px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between",
          background: hovered ? T.bgCard : "transparent", transition: "background 0.15s",
        }}
        onClick={onToggle}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: T.accent, display: "flex" }}>{icon}</span>
          {title}
          {badge && <span style={{ fontSize: 9, padding: "1px 5px", background: T.tealDim, color: T.accent, borderRadius: 3, fontWeight: 700 }}>{badge}</span>}
        </span>
        <span style={{ color: T.textDim, display: "flex", transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>
          {Icons.chevronRight}
        </span>
      </div>
      {expanded && <div style={{ padding: "0 16px 16px", background: T.bg }}>{children}</div>}
    </div>
  );
}

function AssetCard({ title, items }) {
  return (
    <div style={{ background: T.bgCard, borderRadius: 8, padding: 12, marginBottom: 8, border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.accent, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>{title}</div>
      {items && items.map((item, i) => (
        <div key={i} style={{ fontSize: 11, color: T.textSecondary, padding: "3px 0", lineHeight: 1.4 }}>{"\u2022"} {item}</div>
      ))}
    </div>
  );
}

// ============================================================
// HOW-TO TAB
// ============================================================
function GuideCard({ guide, isExpanded, onToggle }) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <div
      style={{
        padding: "14px 16px", borderRadius: 10,
        backgroundColor: isHovered || isExpanded ? T.bgCardHover : T.bgCard,
        border: `1px solid ${isExpanded ? T.accent + "40" : T.border}`,
        cursor: "pointer", transition: "all 0.15s ease", marginBottom: 10,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onToggle}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 20 }}>{guide.icon}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color: T.text, flex: 1 }}>{guide.title}</span>
        <span style={{ color: T.textMuted, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform 0.15s", display: "flex" }}>
          {Icons.chevronDown}
        </span>
      </div>

      {isExpanded && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
          <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {guide.steps.map((step, index) => (
              <li key={index} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10, fontSize: 13, color: T.textSecondary, lineHeight: 1.5 }}>
                <span style={{
                  width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                  backgroundColor: T.accent + "20", color: T.accent,
                  fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center",
                }}>{index + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          {guide.tip && (
            <div style={{
              marginTop: 12, padding: "10px 12px", borderRadius: 8,
              backgroundColor: T.accent + "10", border: `1px solid ${T.accent}30`,
              fontSize: 12, color: T.accent, lineHeight: 1.5,
            }}>
              <span style={{ fontWeight: 600, marginRight: 6 }}>{"\u{1F4A1}"} Tip:</span>{guide.tip}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HowToTab() {
  const [expandedGuide, setExpandedGuide] = useState(null);
  const toggleGuide = (id) => setExpandedGuide(expandedGuide === id ? null : id);

  return (
    <div style={{ padding: "12px 0" }}>
      <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5, marginBottom: 16 }}>
        Quick answers to get you started with VenueFlow.
      </div>

      {GUIDES.map((guide) => (
        <GuideCard
          key={guide.id}
          guide={guide}
          isExpanded={expandedGuide === guide.id}
          onToggle={() => toggleGuide(guide.id)}
        />
      ))}
    </div>
  );
}

// ============================================================
// ENTITLEMENTS: OVERVIEW SUB-TAB
// ============================================================
function EntOverview() {
  return (
    <div style={{ padding: "12px 0" }}>
      <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5, marginBottom: 16 }}>
        2026 Club Commercial Program — key changes from 2025.
      </div>

      {KEY_CHANGES_2026.map((change, idx) => (
        <div key={idx} style={{ background: T.bgCard, borderRadius: 8, padding: 14, marginBottom: 8, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>{change.title}</div>
          <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5, marginBottom: 6 }}>{change.description}</div>
          <div style={{ fontSize: 11, color: T.green, fontWeight: 600 }}>{"\u2713"} {change.benefit}</div>
        </div>
      ))}

      <SubHeader>{"\u{1F6A8}"} Critical Restrictions</SubHeader>
      <RestrictionBadge type="danger">Player Race (MCG): NO commercial content on gameday — no exceptions</RestrictionBadge>
      <RestrictionBadge type="danger">Outside Club Room (MCG): 80% Club / 20% Commercial max — 100% commercial NOT permitted</RestrictionBadge>
      <RestrictionBadge type="warning">Team Entry, Countdown, Club Song: 100% Club IP only — cannot be commercialised</RestrictionBadge>
      <RestrictionBadge type="warning">Half Time on-field: First 5 min reserved for AusKick — no Club activities</RestrictionBadge>
    </div>
  );
}

// ============================================================
// ENTITLEMENTS: GAME PHASES SUB-TAB
// ============================================================
function EntPhases() {
  const [expanded, setExpanded] = useState(["preGame"]);
  const toggle = (s) => setExpanded(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const data = ENTITLEMENTS_2026;

  return (
    <div style={{ margin: "0 -16px" }}>
      <AccordionSection icon={Icons.clock} title="Pre-Game" expanded={expanded.includes("preGame")} onToggle={() => toggle("preGame")}>
        <SubHeader>{data.preGame.gatesTo30Min.label}</SubHeader>
        <InfoRow label="LED SOV" value={`${data.preGame.gatesTo30Min.led.clubSOV}% Club / ${data.preGame.gatesTo30Min.led.aflSOV}% AFL`} highlight />
        <InfoRow label="LED Branding" value="Club IP, Co-branded, Commercial + Overlay" />
        <InfoRow label="Big Screen TVC" value={`${data.preGame.gatesTo30Min.bigScreen.tvcMinutes} min total (${data.preGame.gatesTo30Min.bigScreen.maxTVCsPerPartner}\u00D7 max/partner)`} />
        <InfoRow label="Super Screen" value="Live vision 20:9 or 32:9 aspect" />
        <Note>AFL 10% allocation: promote AFL programs (e.g. AFLW). If unused, reverts to Club.</Note>

        <SubHeader>{data.preGame.within30Min.label}</SubHeader>
        <InfoRow label="LED SOV" value="100% Club" highlight />
        <InfoRow label="Big Screen" value="Club IP, Co-branded, or Branded Sponsored" />
        <Note type="warning">All graphics to be pre-approved by Lumen8.</Note>

        <SubHeader>Key Club Moments</SubHeader>
        <div style={{ fontSize: 11, color: T.textDim, marginBottom: 6 }}>Can be commercialised:</div>
        <TagList>{KEY_MOMENTS.commercialisable.map((m, i) => <Tag key={i} variant="success">{m}</Tag>)}</TagList>
        <div style={{ fontSize: 11, color: T.textDim, marginBottom: 6, marginTop: 10 }}>Must remain 100% Club IP:</div>
        <TagList>{KEY_MOMENTS.clubIPOnly.map((m, i) => <Tag key={i} variant="warning">{m}</Tag>)}</TagList>
        <div style={{ fontSize: 11, color: T.textDim, marginBottom: 6, marginTop: 10 }}>Fan Engagement (Co-branded):</div>
        <TagList>{KEY_MOMENTS.fanEngagement.map((m, i) => <Tag key={i} variant="teal">{m}</Tag>)}</TagList>
      </AccordionSection>

      <AccordionSection icon={Icons.tv} title="In-Game" expanded={expanded.includes("inGame")} onToggle={() => toggle("inGame")}>
        <SubHeader>Club Match Day Package</SubHeader>
        <InfoRow label="LED Rotations" value={`${data.inGame.matchDayPackage.led.rotations} \u00D7 ${data.inGame.matchDayPackage.led.duration}sec`} highlight />
        <InfoRow label="Total LED Time" value={`${data.inGame.matchDayPackage.led.totalMinutes} min (siren to siren)`} />
        <InfoRow label="Big Screen (Club IP)" value={`${data.inGame.matchDayPackage.bigScreen.clubStatic.count} \u00D7 ${data.inGame.matchDayPackage.bigScreen.clubStatic.duration}sec/qtr`} />
        <InfoRow label="Big Screen (Commercial)" value={`${data.inGame.matchDayPackage.bigScreen.commercialStatic.count} \u00D7 ${data.inGame.matchDayPackage.bigScreen.commercialStatic.duration}sec/qtr (max 4/brand)`} />
        <Note type="warning">LED must be bundled into partnerships inclusive of Club IP. Cannot be a straight LED buy or on-sold.</Note>

        <SubHeader>Additional LED</SubHeader>
        <InfoRow label="Extra Rotations" value={`Up to ${data.inGame.additionalPurchases.led.additionalRotations} \u00D7 30sec`} highlight />
        <Note>Must advise Lumen8 by 31 Jan. MCG & Marvel at equal spot weights.</Note>

        <SubHeader>Goal Animation</SubHeader>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 4 }}>
          <AssetCard title="Option A (Preferred)" items={[`${data.inGame.goalAnimation.optionA.ledDuration}sec integrated (30/70)`]} />
          <AssetCard title="Option B (Split)" items={[`${data.inGame.goalAnimation.optionB.ledClubDuration}s Club + ${data.inGame.goalAnimation.optionB.ledSponsorDuration}s Sponsor`]} />
        </div>
      </AccordionSection>

      <AccordionSection icon={Icons.target} title="Quarter Breaks" expanded={expanded.includes("inBreak")} onToggle={() => toggle("inBreak")}>
        <SubHeader>Quarter Time & 3QT</SubHeader>
        <InfoRow label="Activation Time" value="Entirety of break" highlight />
        <InfoRow label="LED SOV" value="100% Club" />
        <InfoRow label="Big Screen TVC" value="Up to 1 min" />

        <SubHeader>Half Time</SubHeader>
        <InfoRow label="Activation Time" value="Final 4 min only" highlight />
        <InfoRow label="AFL Reserved" value="Until 16 min mark (AusKick)" />
        <InfoRow label="AFL LED" value="2 min for AFL programs" />
        <InfoRow label="Big Screen TVC" value="Up to 2 min" />
        <RestrictionBadge type="warning">No on-field activation without AFL approval. No conflict with AusKick partner.</RestrictionBadge>
      </AccordionSection>

      <AccordionSection icon={Icons.flag} title="Post-Game" expanded={expanded.includes("postGame")} onToggle={() => toggle("postGame")}>
        <SubHeader>Final Siren {"\u2192"} 30 min</SubHeader>
        <InfoRow label="LED SOV" value="100% Club" highlight />
        <div style={{ fontSize: 11, color: T.textDim, marginTop: 10, marginBottom: 6 }}>Commercialisable:</div>
        <TagList>{data.postGame.sirenTo30Min.commercialisableMoments.map((m, i) => <Tag key={i} variant="success">{m}</Tag>)}</TagList>
        <div style={{ fontSize: 11, color: T.textDim, marginTop: 10, marginBottom: 6 }}>100% Club IP only:</div>
        <TagList>{data.postGame.sirenTo30Min.clubIPOnlyMoments.map((m, i) => <Tag key={i} variant="warning">{m}</Tag>)}</TagList>

        <SubHeader><Tag variant="success">NEW</Tag> Post-Game Activation</SubHeader>
        <InfoRow label="Duration" value={`${data.postGame.activation.duration} min`} highlight />
        <InfoRow label="Timing" value="After host wrap-up" />
        <Note type="warning">Must have fan engagement outcome. Cannot conflict with K2K partner.</Note>
      </AccordionSection>
    </div>
  );
}

// ============================================================
// ENTITLEMENTS: OTHER ASSETS SUB-TAB
// ============================================================
function EntAssets() {
  const [expanded, setExpanded] = useState(["venueAssets"]);
  const toggle = (s) => setExpanded(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const data = ENTITLEMENTS_2026.otherAssets;

  return (
    <div style={{ margin: "0 -16px" }}>
      <AccordionSection icon={Icons.layout} title="Venue Assets" expanded={expanded.includes("venueAssets")} onToggle={() => toggle("venueAssets")}>
        <SubHeader>{data.playersRace.label}</SubHeader>
        <RestrictionBadge type="danger">NO commercial content on Player Race LED on gameday — no exceptions</RestrictionBadge>
        <InfoRow label="Branding" value="100% Club IP ONLY" />
        <Note type="danger">Strict MCG/AFL guideline. Escalate to Commercial Ops immediately if clubs supply commercial content.</Note>

        <SubHeader>{data.outsideClubRoom.label}</SubHeader>
        <RestrictionBadge type="danger">80% Club / 20% Commercial max — 100% commercial NOT permitted</RestrictionBadge>
        <InfoRow label="Branding" value="Co-branded (80/20 rule)" />
        <Note type="danger">Visible to broadcast cameras. Escalate if clubs supply 100% commercial content.</Note>

        <SubHeader>{data.interchange.label}</SubHeader>
        <InfoRow label="Signage" value="Home, Away & Umpire Bench" />
        <InfoRow label="LED Parapet" value="~10sec every 120sec during play" />
        <InfoRow label="Max Partners" value={`${data.interchange.maxPartners} per game`} />

        <SubHeader>{data.umpiresRace.label}</SubHeader>
        <InfoRow label="Cost" value={data.umpiresRace.cost} />
        <InfoRow label="Branding" value={data.umpiresRace.branding} />
        <Note type="warning">Cannot conflict with AFL Partner. Club responsible for removal post game day.</Note>

        <SubHeader>{data.mcDesk.label}</SubHeader>
        <InfoRow label="Branding" value={data.mcDesk.branding} />
        <InfoRow label="Cost" value={data.mcDesk.cost} />
      </AccordionSection>

      <AccordionSection icon={Icons.monitor} title="Screens & Stages" expanded={expanded.includes("screens")} onToggle={() => toggle("screens")}>
        <SubHeader>{data.iptv.label}</SubHeader>
        <InfoRow label="Club SOV" value={`${data.iptv.clubSOV}% available`} highlight />
        <InfoRow label="Example" value={data.iptv.example} />
        <div style={{ marginTop: 6 }}>
          <span style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase" }}>Formats</span>
          <TagList>{data.iptv.formats.map((f, i) => <Tag key={i} variant="teal">{f}</Tag>)}</TagList>
        </div>
        <div style={{ marginTop: 6 }}>
          <span style={{ fontSize: 10, color: T.textDim, textTransform: "uppercase" }}>Restrictions</span>
          <TagList>{data.iptv.restrictions.map((r, i) => <Tag key={i} variant="danger">{r}</Tag>)}</TagList>
        </div>
        <BrandingOptions options={data.iptv.brandingOptions} />

        <SubHeader>{data.ponsfordStage.label}</SubHeader>
        <InfoRow label="Availability" value={data.ponsfordStage.availability} />
        <InfoRow label="LED In-Break" value={data.ponsfordStage.ledUsage.inBreak} />
        <InfoRow label="LED In-Game" value={data.ponsfordStage.ledUsage.inGame} />
        <InfoRow label="Hire Fee" value={data.ponsfordStage.hireFee} mono />
        <Note type="warning">LED not available in-game. Unavailable for marquee games (ANZAC, Dreamtime, etc).</Note>
      </AccordionSection>

      <AccordionSection icon={Icons.users} title="Activations & Partners" expanded={expanded.includes("activations")} onToggle={() => toggle("activations")}>
        <SubHeader>In-Bowl & On-Field</SubHeader>
        <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5, marginBottom: 8 }}>
          Pre-match/QT/3QT: Clubs have first right. Non-conflicting partner activations require value exchange via Lumen8.
        </div>
        <RestrictionBadge type="danger">Half Time: First 5 min clear for AusKick — no Club activities on-field</RestrictionBadge>
        <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5, marginBottom: 8 }}>
          Post-Match: AFL exclusive right to Kick 2 Kick for min 15 min.
        </div>

        <SubHeader>Protected Partners</SubHeader>
        <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5 }}>
          Club Partners at Major/Gold thresholds can co-exist with Lumen8 Protected Partners. Best endeavours to not place competing brands on Club LED.
        </div>
        <Note type="warning">Lumen8 provides protected partners list by 31 Jan. Clubs provide updated partner list to Lumen8.</Note>
      </AccordionSection>
    </div>
  );
}

// ============================================================
// ENTITLEMENTS TAB (with sub-navigation)
// ============================================================
const ENT_SUB_TABS = [
  { key: "overview", label: "Overview" },
  { key: "phases", label: "Game Phases" },
  { key: "assets", label: "Other Assets" },
];

function EntitlementsTab() {
  const [subTab, setSubTab] = useState("overview");

  return (
    <div>
      {/* Sub-tab navigation */}
      <div style={{ display: "flex", gap: 4, marginBottom: 12, padding: "12px 0 0" }}>
        {ENT_SUB_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            style={{
              flex: 1, padding: "7px 8px", border: "none", cursor: "pointer", borderRadius: 6,
              background: subTab === tab.key ? T.tealDim : "transparent",
              color: subTab === tab.key ? T.accent : T.textMuted,
              fontSize: 11, fontWeight: 600, transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === "overview" && <EntOverview />}
      {subTab === "phases" && <EntPhases />}
      {subTab === "assets" && <EntAssets />}
    </div>
  );
}

// ============================================================
// MAIN TABS
// ============================================================
const MAIN_TABS = [
  { key: "howto", label: "How To", emoji: "\u{1F4D6}" },
  { key: "entitlements", label: "2026 Rules", emoji: "\u{1F6E1}\uFE0F" },
];

// ============================================================
// MAIN COMPONENT
// ============================================================
export function HelpWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [activeTab, setActiveTab] = useState("howto");

  const togglePanel = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Panel */}
      {isOpen && (
        <div style={{
          position: "fixed", bottom: 96, right: 24,
          width: 440, maxHeight: "calc(100vh - 140px)",
          backgroundColor: T.bgElevated, borderRadius: 16,
          boxShadow: "0 10px 50px rgba(0, 0, 0, 0.5)",
          border: `1px solid ${T.border}`,
          overflow: "hidden", zIndex: 9998,
          display: "flex", flexDirection: "column",
          fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Inter', sans-serif",
        }}>
          {/* Header */}
          <div style={{
            padding: "16px 20px", borderBottom: `1px solid ${T.border}`,
            display: "flex", alignItems: "center", gap: 12,
            background: `linear-gradient(135deg, ${T.bgElevated} 0%, ${T.bgCard} 100%)`,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${T.accent}20 0%, ${T.lime}20 100%)`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>Help & Guidelines</h3>
              <p style={{ fontSize: 12, color: T.textMuted, margin: 0 }}>Guides, rules & entitlements</p>
            </div>
            <button
              onClick={togglePanel}
              style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer", padding: 4, display: "flex" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Main Tabs */}
          <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, padding: "0" }}>
            {MAIN_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  flex: 1, padding: "12px 8px", border: "none", cursor: "pointer",
                  background: activeTab === tab.key ? T.bgCard : "transparent",
                  color: activeTab === tab.key ? T.accent : T.textMuted,
                  borderBottom: activeTab === tab.key ? `2px solid ${T.accent}` : "2px solid transparent",
                  fontSize: 13, fontWeight: 600, transition: "all 0.15s",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <span>{tab.emoji}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px 20px" }}>
            {activeTab === "howto" && <HowToTab />}
            {activeTab === "entitlements" && <EntitlementsTab />}
          </div>

          {/* Footer */}
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${T.border}`, textAlign: "center" }}>
            <p style={{ fontSize: 12, color: T.textMuted, margin: 0 }}>
              Need help?{" "}
              <a href="mailto:bridget.ogrady@lumen8.com.au" style={{ color: T.accent, textDecoration: "none", fontWeight: 500 }}>
                Contact Lumen8
              </a>
            </p>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={togglePanel}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9999,
          width: 56, height: 56, borderRadius: "50%",
          background: `linear-gradient(135deg, ${T.accent} 0%, ${T.lime} 100%)`,
          border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: isHovered ? "0 6px 28px rgba(47, 214, 181, 0.5)" : "0 4px 20px rgba(47, 214, 181, 0.4)",
          transform: isHovered ? "scale(1.08)" : isOpen ? "rotate(45deg)" : "scale(1)",
          transition: "all 0.2s ease",
        }}
        aria-label={isOpen ? "Close help" : "Open help & guidelines"}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={T.bg} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {isOpen ? (
            <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>
          ) : (
            <><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></>
          )}
        </svg>
      </button>
    </>
  );
}

export default HelpWidget;
